import { stripInlineMarkdown } from "@/lib/MarkdownInline";

export interface MarkdownHeading {
  level: 1 | 2 | 3;
  text: string;
  id: string;
}

const HEADING_PREFIXES: ReadonlyArray<{ level: 1 | 2 | 3; prefix: string }> = [
  { level: 3, prefix: "### " },
  { level: 2, prefix: "## " },
  { level: 1, prefix: "# " },
];

export function extractMarkdownHeadings(markdown: string): MarkdownHeading[] {
  const usedIdCounts = new Map<string, number>();

  return markdown
    .split(/\r?\n/)
    .map(parseHeadingLine)
    .filter(
      (heading): heading is { level: 1 | 2 | 3; text: string } =>
        heading !== null,
    )
    .map(({ level, text }) => ({
      level,
      text,
      id: nextUniqueId(text, usedIdCounts),
    }));
}

function parseHeadingLine(
  line: string,
): { level: 1 | 2 | 3; text: string } | null {
  const matched = HEADING_PREFIXES.find(({ prefix }) =>
    line.startsWith(prefix),
  );
  if (matched === undefined) {
    return null;
  }
  return {
    level: matched.level,
    text: stripInlineMarkdown(line.slice(matched.prefix.length)),
  };
}

function nextUniqueId(text: string, usedIdCounts: Map<string, number>): string {
  const baseId = toId(text);
  const occurrence = usedIdCounts.get(baseId) ?? 0;
  usedIdCounts.set(baseId, occurrence + 1);
  return occurrence === 0 ? baseId : `${baseId}-${occurrence + 1}`;
}

function toId(text: string): string {
  return text.trim().replace(/#/g, "").replace(/\s+/g, "-");
}
